import React, { useCallback } from "react";
import {
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import AsyncSelect from 'react-select/async';
import { Formik } from 'formik';
import { API_ROUTES } from 'src/config/api';
import { X } from 'lucide-react';

import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Auth } from "aws-amplify";
import Axios from "axios";
import { useHistory } from "react-router-dom";
import { fetchOrganisations, setOrganisation } from 'src/actions/organisationActions';
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "src/components/Component";

const CreateOrganisation = ({ isOpen, toggle }) => {
  const { organisation } = useSelector((state) => state);
  const history = useHistory();
  const dispatch = useDispatch();

  const findOrganisations = useCallback(async (value) => {
    const response = [];

    organisation.organisations.forEach((item) => {
      if (item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1 || item.id.toLowerCase().indexOf(value.toLowerCase()) !== -1) {
        response.push(item);
      }
    });

    return response;
  }, [organisation]);

  return (
    <Formik
      initialValues={{
        id: '',
        name: '',
        parent: '',
      }}
      validationSchema={Yup.object().shape({
        id: Yup.string()
          .max(10, 'Organisation ID must be at most 10 characters')
          .matches(/^\S*$/, 'Organisation ID must not contain spaces')
          .required('Organisation ID is required'),
        name: Yup.string().max(50).required('Organisation Name is required'),
        parent: Yup.string().required('Parent organisation is required')
      })}
      onSubmit={async (values, { resetForm }) => {
        const user = await Auth.currentAuthenticatedUser();

        await Axios(
          API_ROUTES.user['organisations:create'](),
          {
            method: 'put',
            data: values,
            headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          }
        );

        await dispatch(fetchOrganisations());
        await dispatch(setOrganisation(values.id.toUpperCase()));

        resetForm();
        toggle();
        history.push(`/organisation/settings`);
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
        resetForm,
      }) => (
        <Modal isOpen={isOpen} toggle={() => !isSubmitting && toggle()} className="modal-dialog-centered" size="lg">
          <form noValidate onSubmit={handleSubmit}>
            <ModalHeader
              className="bg-red-500 text-2xl text-white"
              close={
                <button className="mt-0.5 text-white" disabled={isSubmitting} onClick={(ev) => {
                  ev.preventDefault();
                  if (!isSubmitting) {
                    resetForm();
                    toggle();
                  }
                }}>
                  <XMarkIcon className="h-6 w-6 mt-1" />
                </button>
              }
            >
               <strong>Create</strong> Organisation
            </ModalHeader>
            <ModalBody>
              <div className="px-2 space-y-4 py-2">

                {/* Organisation ID */}
                <div>
                  <label className="block font-bold text-purple-900 mb-1" htmlFor="id">
                    Organisation ID
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    The unique identifier for this organisation (max 10 characters, no spaces)
                  </p>
                  <input
                    type="text"
                    id="id"
                    name="id"
                    value={values.id}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const noSpaces = e.target.value.replace(/\s/g, '');
                      setFieldValue('id', noSpaces);
                    }}
                    maxLength={10}
                    className={`border px-3 py-2 w-full rounded-md ${
                      touched.id && errors.id ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g. ACME"
                  />
                  {touched.id && errors.id && (
                    <p className="text-sm text-red-600 mt-1">{errors.id}</p>
                  )}
                </div>

                {/* Organisation Name */}
                <div>
                  <label className="block font-bold text-purple-900 mb-1" htmlFor="name">
                    Organisation Name
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    The display name of the organisation
                  </p>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={values.name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={50}
                    className={`border px-3 py-2 w-full rounded-md ${
                      touched.name && errors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g. Acme Travel Ltd"
                  />
                  {touched.name && errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Parent Organisation */}
                <div>
                  <label className="block font-bold text-purple-900 mb-1">
                    Parent Organisation
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    The over-arching parent of the organisation
                  </p>

                  {values.parent !== '' && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-800 font-medium px-4 py-2 rounded-md border border-violet-200">
                        {values.parent}
                        <button
                          type="button"
                          onClick={() => setFieldValue("parent", '')}
                          className="text-violet-600 hover:text-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    </div>
                  )}

                  {values.parent === '' && (
                    <FormGroup className="mb-0">
                      <div className="form-control-wrap">
                        <AsyncSelect
                          isClearable
                          name="parent"
                          id="parent"
                          className="form-control form-control-xl p-0 border-0 indicator-hidden"
                          value={values.parent}
                          getOptionLabel={e => `${e.name} | ${e.id}`}
                          getOptionValue={e => e.id}
                          loadOptions={findOrganisations}
                          onChange={(opt) => {
                            setFieldValue("parent", opt.id);
                          }}
                          placeholder="Search for a parent organisation..."
                          classNamePrefix="react-select"
                        />
                        {touched.parent && errors.parent && (
                          <p className="text-sm text-red-600 mt-1">{errors.parent}</p>
                        )}
                      </div>
                    </FormGroup>
                  )}
                </div>

              </div>
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button className="bg-violet-700 hover:bg-violet-800 text-white mb-0" size="md" type="submit" disabled={Object.keys(errors).length > 0 || isSubmitting}>
                {isSubmitting ? (
                  <Spinner size="sm">{''}</Spinner>
                ) : (
                  'Create Organisation'
                )}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      )}
    </Formik>
  );
};

export default CreateOrganisation;
